import { createLogger } from '@/lib/logger'

/**
 * File API service for managing file uploads and signed URLs.
 * @module lib/api/file.api
 */

import { apiClient } from './api-client'
import { createSingletonApi } from './singleton-factory'

const logger = createLogger('lib/api/file.api')

export interface FileUploadResponse {
  key: string
  url: string
  thumbnailUrl?: string
  fileName: string
  contentType: string
  size: number
}

// Must stay comfortably below the minimum remaining validity the API
// guarantees for signed URLs it serves (35 minutes — see
// FileService.MIN_REMAINING_URL_VALIDITY), so a cached URL can't expire
// while we're still handing it out.
const SIGNED_URL_CACHE_DURATION = 20 * 60 * 1000 // 20 minutes

class FileApiService {
  private signedUrlCache = new Map<string, { url: string; expiresAt: number }>()
  private signedUrlRequests = new Map<string, Promise<string | null>>()

  /**
   * Uploads a file to storage
   * @param moduleType - Type of module the file belongs to
   * @param id - Module ID
   * @param file - File to upload
   * @param _fileName - Original file name (unused, kept for compatibility)
   * @param _contentType - Content type (unused, kept for compatibility)
   * @param options - Upload options
   * @returns Upload response with file details
   */
  async uploadFile(
    moduleType: string,
    id: string,
    file: File,
    _fileName: string,
    _contentType: string,
    options:
      | { isAsset?: boolean; coverPhoto?: boolean; skipDocument?: boolean }
      | boolean = false,
  ): Promise<FileUploadResponse> {
    // Handle backwards compatibility where options was just isAsset boolean
    const opts = typeof options === 'boolean' ? { isAsset: options } : options

    const formData = new FormData()
    formData.append('file', file)

    const params = new URLSearchParams()
    if (opts.isAsset) params.append('asset', 'true')
    if (opts.coverPhoto) params.append('coverPhoto', 'true')
    // Stage without a Document record (ticket message attachments are tracked
    // as CommunicationAttachment instead).
    if (opts.skipDocument) params.append('document', 'false')

    const queryString = params.toString()
    const url = `/files/upload/${moduleType}/${id}${queryString ? `?${queryString}` : ''}`

    return apiClient<FileUploadResponse>(url, {
      method: 'POST',
      body: formData,
      headers: {},
    })
  }

  /**
   * Gets a signed URL for a file
   * @param moduleType - Type of module
   * @param id - Module ID
   * @param prefix - File prefix
   * @param fileName - File name
   * @returns Signed URL
   */
  async getSignedUrl(
    moduleType: string,
    id: string,
    prefix: string,
    fileName: string,
  ): Promise<{ url: string }> {
    return apiClient<{ url: string }>(
      `/files/${moduleType}/${id}/${prefix}/${encodeURIComponent(fileName)}/signed-url`,
    )
  }

  /**
   * Gets multiple signed URLs for file keys with caching
   * @param keys - Array of file keys
   * @param options - forceRefresh bypasses both the local cache and the
   *   server-side URL cache, guaranteeing freshly signed URLs (used when
   *   recovering from an image that failed to load)
   * @returns Map of keys to signed URLs
   */
  async getMultipleSignedUrls(
    keys: string[],
    options: { forceRefresh?: boolean } = {},
  ): Promise<Record<string, string>> {
    if (!keys || keys.length === 0) {
      return {}
    }

    const now = Date.now()
    const result: Record<string, string> = {}
    const uniqueKeys = Array.from(
      new Set(keys.filter(key => typeof key === 'string' && key.trim())),
    )
    const keysToFetch: string[] = []
    const pendingResults: Array<Promise<void>> = []

    // Check cache first
    uniqueKeys.forEach(key => {
      const cached = options.forceRefresh
        ? undefined
        : this.signedUrlCache.get(key)
      if (cached && cached.expiresAt > now) {
        result[key] = cached.url
      } else if (!options.forceRefresh && this.signedUrlRequests.has(key)) {
        const pending = this.signedUrlRequests.get(key)!
        pendingResults.push(
          pending.then(url => {
            if (url) {
              result[key] = url
            }
          }),
        )
      } else {
        keysToFetch.push(key)
        if (cached) {
          this.signedUrlCache.delete(key)
        }
      }
    })

    // Fetch uncached URLs
    if (keysToFetch.length > 0) {
      const expiresAt = now + SIGNED_URL_CACHE_DURATION
      const request = apiClient<{ signedUrls: Record<string, string> }>(
        '/files/signed-urls',
        {
          method: 'POST',
          body: options.forceRefresh
            ? { keys: keysToFetch, forceRefresh: true }
            : { keys: keysToFetch },
        },
      )
        .then(response => {
          Object.entries(response.signedUrls).forEach(([key, url]) => {
            this.signedUrlCache.set(key, { url, expiresAt })
          })

          return response.signedUrls
        })
        .catch(error => {
          logger.error('[FileApi] Error fetching signed URLs:', error)

          return {}
        })

      const perKeyRequests = keysToFetch.map(key => {
        const promise = request
          .then(signedUrls => signedUrls[key] || null)
          .finally(() => {
            this.signedUrlRequests.delete(key)
          })
        this.signedUrlRequests.set(key, promise)

        return promise.then(url => {
          if (url) {
            result[key] = url
          }
        })
      })

      await Promise.all(perKeyRequests)
    }

    if (pendingResults.length > 0) {
      await Promise.all(pendingResults)
    }

    return result
  }

  /**
   * Clears cached signed URLs
   * @param keys - Optional specific keys to clear, or all if not provided
   */
  clearSignedUrlCache(keys?: string[]): void {
    if (keys) {
      keys.forEach(key => this.signedUrlCache.delete(key))
    } else {
      this.signedUrlCache.clear()
    }
  }

  /**
   * Force refreshes signed URLs by clearing cache and fetching new ones
   * @param keys - Keys to refresh
   * @returns Fresh signed URLs
   */
  async refreshSignedUrls(keys: string[]): Promise<Record<string, string>> {
    keys.forEach(key => this.signedUrlCache.delete(key))
    return this.getMultipleSignedUrls(keys, { forceRefresh: true })
  }

  /**
   * Deletes a file from storage
   * @param key - File key to delete
   * @returns Deletion result
   */
  async deleteFile(
    key: string,
  ): Promise<{ success: boolean; message: string }> {
    const response = await apiClient<{ success: boolean; message: string }>(
      `/files/${encodeURIComponent(key)}`,
      { method: 'DELETE' },
    )

    this.clearSignedUrlCache([key])
    return response
  }
}

export const fileApi = createSingletonApi(FileApiService)
